import { z } from 'zod';
import { BOOK_SIZE_OPTIONS, DEFAULT_BOOK_SIZE } from './bookSizes.js';
import { SUPPORTED_BOOK_LANGUAGES } from './supportedLanguages.js';
import {
  SURVEY_FIELD_LIMITS,
  TEXT_FIELD_CONSTRAINTS,
  getPageCountLimits,
} from './validationLimits.js';
import { ART_STYLE_OPTIONS } from './artStyles.js';
import { COLOR_PALETTE_OPTIONS } from './colorPalettes.js';
import { TEXT_DENSITY_KEYS } from './textDensity.js';

export const CHILD_PERSONALITY_TRAITS = [
  'brave_adventurer',
  'creative_artist',
  'curious_explorer',
  'loyal_friend',
  'problem_solver',
  'kind_helper',
  'funny_comedian',
  'nature_lover',
  'sports_star',
  'bookworm',
  'animal_champion',
  'science_explorer',
  'musical_magic',
] as const;

/**
 * Predefined story theme suggestions.
 * Note: theme field accepts ANY string, these are just common examples.
 */
export const STORY_THEME_OPTIONS = [
  'space_explorer',
  'magical_kingdom',
  'dinosaur_discovery',
  'ocean_adventure',
  'superhero_mission',
  'fairy_forest',
  'mystery_quest',
  'pirate_treasure',
  'time_travel',
  'eco_guardian',
  'sports_champion',
  'robot_inventor',
  'family_history',
  'birthday_adventure',
  'bedtime_journey',
] as const;

/**
 * Type that allows predefined themes with autocomplete + any custom string.
 * The `& {}` trick enables TypeScript to suggest predefined options while accepting any string.
 */
export type StoryTheme = (typeof STORY_THEME_OPTIONS)[number] | (string & {});

/**
 * Predefined plot type suggestions.
 * Note: plot field accepts ANY string, these are just common examples.
 */
export const STORY_PLOT_OPTIONS = [
  'hero_journey',
  'biographical',
  'educational',
  'adventure_quest',
  'friendship_tale',
  'discovery_story',
  'overcoming_fears',
  'celebration',
  'custom',
] as const;

/**
 * Type that allows predefined plots with autocomplete + any custom string.
 */
export type StoryPlot = (typeof STORY_PLOT_OPTIONS)[number] | (string & {});

/**
 * Combined story idea options: all themes + all plots + custom.
 * Used in the merged "What will the story be about?" step.
 * plotType field can now accept any value from this combined list.
 */
export const STORY_IDEA_OPTIONS = [
  ...STORY_THEME_OPTIONS,
  ...STORY_PLOT_OPTIONS,
] as const;

// Re-export style options from dedicated files
export { ART_STYLE_OPTIONS } from './artStyles.js';
export { COLOR_PALETTE_OPTIONS } from './colorPalettes.js';
export {
  BORDER_STYLE_OPTIONS,
  BORDER_STYLE_KEYS,
} from './borderDecorations.js';
export { TEXT_DENSITY_KEYS } from './textDensity.js';

// Re-export types
export type { ArtStyleKey } from './artStyles.js';
export type { ColorPaletteKey } from './colorPalettes.js';
export type { BorderStyleKey } from './borderDecorations.js';
export type { TextDensityKey } from './textDensity.js';

// Avatar character appearance options
export const AVATAR_ETHNICITIES = [
  'caucasian',
  'african',
  'asian',
  'hispanic',
  'middle_eastern',
  'south_asian',
] as const;

export const AVATAR_SEXES = ['boy', 'girl', 'nb'] as const;

export const AVATAR_HAIR_TYPES = ['straight', 'curly'] as const;

export const AVATAR_HAIR_COLORS = ['blonde', 'brown', 'black', 'red'] as const;

// Story-themed names for avatars (maps ethnicity to friendly name)
export const AVATAR_STORY_NAMES: Record<
  (typeof AVATAR_ETHNICITIES)[number],
  string
> = {
  caucasian: 'sunny_explorer',
  african: 'midnight_adventurer',
  asian: 'cherry_blossom',
  hispanic: 'golden_star',
  middle_eastern: 'desert_rose',
  south_asian: 'lotus_dream',
} as const;

// Generate all avatar style options programmatically
function generateAvatarStyleOptions(): string[] {
  const options: string[] = [];
  for (const ethnicity of AVATAR_ETHNICITIES) {
    const storyName = AVATAR_STORY_NAMES[ethnicity];
    for (const sex of AVATAR_SEXES) {
      for (const hairType of AVATAR_HAIR_TYPES) {
        for (const hairColor of AVATAR_HAIR_COLORS) {
          options.push(`${storyName}_${sex}_${hairType}_${hairColor}`);
        }
      }
    }
  }
  return options;
}

export const AVATAR_STYLE_OPTIONS = [
  ...generateAvatarStyleOptions(),
  'custom_upload',
] as const;

export type AvatarSex = (typeof AVATAR_SEXES)[number];
export type AvatarHairType = (typeof AVATAR_HAIR_TYPES)[number];
export type AvatarHairColor = (typeof AVATAR_HAIR_COLORS)[number];
export type AvatarEthnicity = (typeof AVATAR_ETHNICITIES)[number];

/**
 * Skin-tone hint per ethnicity. The avatar enum encodes ethnicity via the
 * story-name prefix but doesn't carry a literal skin-tone token; this map is
 * the canonical decomposition used to anchor character-sheet generation.
 */
export const AVATAR_SKIN_TONE_HINTS: Record<AvatarEthnicity, string> = {
  caucasian: 'fair',
  african: 'deep brown',
  asian: 'light tan',
  hispanic: 'warm tan',
  middle_eastern: 'warm olive',
  south_asian: 'medium brown',
};

export interface AvatarAppearance {
  /** The full avatar enum value, e.g. 'midnight_adventurer_boy_curly_brown'. */
  avatarStyle: string;
  /** Story-name prefix, e.g. 'midnight_adventurer'. */
  storyName: string;
  ethnicity: AvatarEthnicity;
  sex: AvatarSex;
  hairType: AvatarHairType;
  hairColor: AvatarHairColor;
  /** Human-readable skin-tone hint derived from `ethnicity`. */
  skinTone: string;
}

/**
 * Decompose an avatarStyle enum value into structured appearance traits.
 * Returns null for `'custom_upload'`, nullish input, or any value that does
 * not match the `{storyName}_{sex}_{hairType}_{hairColor}` shape — callers
 * should treat null as "no stock-avatar appearance to anchor on".
 */
export function parseAvatarAppearance(
  avatarStyle: string | null | undefined
): AvatarAppearance | null {
  if (!avatarStyle || avatarStyle === 'custom_upload') return null;
  for (const ethnicity of AVATAR_ETHNICITIES) {
    const storyName = AVATAR_STORY_NAMES[ethnicity];
    const prefix = `${storyName}_`;
    if (!avatarStyle.startsWith(prefix)) continue;
    const parts = avatarStyle.slice(prefix.length).split('_');
    if (parts.length !== 3) continue;
    const [sex, hairType, hairColor] = parts;
    if (
      !AVATAR_SEXES.includes(sex as AvatarSex) ||
      !AVATAR_HAIR_TYPES.includes(hairType as AvatarHairType) ||
      !AVATAR_HAIR_COLORS.includes(hairColor as AvatarHairColor)
    ) {
      continue;
    }
    return {
      avatarStyle,
      storyName,
      ethnicity,
      sex: sex as AvatarSex,
      hairType: hairType as AvatarHairType,
      hairColor: hairColor as AvatarHairColor,
      skinTone: AVATAR_SKIN_TONE_HINTS[ethnicity],
    };
  }
  return null;
}

/**
 * Build a human-readable appearance phrase for prompt injection.
 * Example: "a boy with curly brown hair and deep brown skin".
 */
export function describeAvatarAppearance(appearance: AvatarAppearance): string {
  const childWord =
    appearance.sex === 'boy'
      ? 'boy'
      : appearance.sex === 'girl'
        ? 'girl'
        : 'non-binary child';
  return `a ${childWord} with ${appearance.hairType} ${appearance.hairColor} hair and ${appearance.skinTone} skin`;
}

export const MORAL_LESSON_OPTIONS = [
  'friendship',
  'courage',
  'kindness',
  'bravery',
  'creativity',
  'confidence',
  'honesty',
  'wisdom',
  'family',
] as const;

export const MAGICAL_COMPANION_OPTIONS = [
  'wise_owl',
  'playful_dragon',
  'clever_fox',
  'loyal_dog',
  'brave_lion',
  'gentle_elephant',
] as const;

/** Human-readable display names for magical companion enum values. */
export const MAGICAL_COMPANION_DISPLAY_NAMES: Record<
  (typeof MAGICAL_COMPANION_OPTIONS)[number],
  string
> = {
  wise_owl: 'Wise Owl',
  playful_dragon: 'Playful Dragon',
  clever_fox: 'Clever Fox',
  loyal_dog: 'Loyal Dog',
  brave_lion: 'Brave Lion',
  gentle_elephant: 'Gentle Elephant',
};

export const REAL_WORLD_COMPANION_TYPES = [
  'sibling',
  'best_friend',
  'cat',
  'dog',
  'pet',
  'family_member',
] as const;

/**
 * Predefined favorite activities suggestions.
 * Note: favoriteActivities field accepts ANY strings, these are just common examples.
 */
export const FAVORITE_ACTIVITIES_OPTIONS = [
  'reading',
  'drawing',
  'playing_outside',
  'building_legos',
  'playing_sports',
  'cooking',
  'dancing',
  'singing',
  'playing_video_games',
  'swimming',
  'riding_bike',
  'playing_with_pets',
  'crafting',
  'gardening',
  'watching_movies',
  'playing_pretend',
  'climbing_trees',
  'collecting_things',
  'exploring_nature',
  'doing_puzzles',
] as const;

export const EXPERIENCE_FORMAT_OPTIONS = [
  'digital_storybook',
  'premium_hardcover',
  'deluxe_gift_set',
] as const;

/**
 * Purchase type options for the book product.
 * Each tier includes all digital deliverables plus physical book options.
 *
 * Pricing (base price + per-extra-page cost):
 * - digital_only: $29 base for 16 pages + $0.50/extra page (PDF + flipbook)
 * - softcover: $49 base for 16 pages + $0.50/extra page - Softcover printed book + all digital deliverables
 * - hardcover: $69 base for 24 pages + $0.50/extra page - Premium hardcover book + all digital deliverables
 */
export const PURCHASE_TYPE_OPTIONS = [
  'digital_only',
  'softcover',
  'hardcover',
] as const;

/**
 * Type representing valid purchase type values.
 * Derived from PURCHASE_TYPE_OPTIONS for type safety.
 */
export type PurchaseType = (typeof PURCHASE_TYPE_OPTIONS)[number];

const personalityTraitSchema = z.enum(CHILD_PERSONALITY_TRAITS);
const interestSchema = z.enum([
  'animals',
  'sports',
  'art',
  'science',
  'music',
  'vehicles',
  'nature',
  'puzzles',
]);

const realWorldCompanionSchema = z.object({
  type: z.enum(REAL_WORLD_COMPANION_TYPES),
  name: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.companionName.min)
    .max(TEXT_FIELD_CONSTRAINTS.companionName.max),
  age: z.number().int().min(0).max(99).optional(),
  relationship: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.companionRelationship.min)
    .max(TEXT_FIELD_CONSTRAINTS.companionRelationship.max)
    .optional(),
  petType: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.companionPetType.min)
    .max(TEXT_FIELD_CONSTRAINTS.companionPetType.max)
    .optional(),
  description: z
    .string()
    .max(TEXT_FIELD_CONSTRAINTS.companionDescription.max)
    .optional(),
});

export const childProfileSchema = z.object({
  name: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.childName.min)
    .max(TEXT_FIELD_CONSTRAINTS.childName.max),
  nickname: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.nickname.min)
    .max(TEXT_FIELD_CONSTRAINTS.nickname.max)
    .optional(),
  /** Age 3-99 to support books for all ages (yes, even grandpa!) */
  age: z.number().int().min(3).max(99),
  pronouns: z
    .string()
    .trim()
    .min(TEXT_FIELD_CONSTRAINTS.pronouns.min)
    .max(TEXT_FIELD_CONSTRAINTS.pronouns.max)
    .regex(
      TEXT_FIELD_CONSTRAINTS.pronouns.pattern,
      TEXT_FIELD_CONSTRAINTS.pronouns.patternMessage
    ),
  personalityTraits: z
    .array(personalityTraitSchema)
    .min(SURVEY_FIELD_LIMITS.personalityTraits.min)
    .max(SURVEY_FIELD_LIMITS.personalityTraits.max),
  interests: z
    .array(interestSchema)
    .max(SURVEY_FIELD_LIMITS.interests.max)
    .optional(),
  favoriteActivities: z.array(z.string().min(2).max(40)).max(3).optional(),
  avatarStyle: z
    .enum(AVATAR_STYLE_OPTIONS as unknown as [string, ...string[]])
    .optional(),
});

export const storyPreferencesSchema = z.object({
  /**
   * Story theme - accepts any string. See STORY_THEME_OPTIONS for suggestions.
   * Examples: 'space_explorer', 'family_history', 'my_custom_theme'
   */
  theme: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.theme.min)
    .max(TEXT_FIELD_CONSTRAINTS.theme.max)
    .optional(),
  artStyle: z.enum(ART_STYLE_OPTIONS),
  colorPalette: z.enum(COLOR_PALETTE_OPTIONS),
  moralLessons: z
    .array(z.enum(MORAL_LESSON_OPTIONS))
    .min(SURVEY_FIELD_LIMITS.moralLessons.min)
    .max(SURVEY_FIELD_LIMITS.moralLessons.max),
  narrativeVoice: z.enum(['playful', 'soothing', 'dramatic', 'uplifting']),
  pacing: z.enum(['calm', 'energetic', 'mystery']),
  targetStoryLength: z.number().int().min(4).max(48),
  bookLanguage: z.enum(SUPPORTED_BOOK_LANGUAGES).default('English'),
  /**
   * Page border style - may be undefined → AI picks a tasteful border based on
   * the chosen art style during character-sheet generation. When defined, it
   * can be a preset key from BORDER_STYLE_OPTIONS, the literal `'no_border'`
   * (or legacy alias `'none'`), or a custom free-text description (e.g.
   * 'decorated border with pomegranate fruits').
   */
  pageBorderStyle: z
    .string()
    .max(TEXT_FIELD_CONSTRAINTS.pageBorderStyle.max)
    .optional(),
  textDensity: z.enum(TEXT_DENSITY_KEYS).default('medium'),
  photoUrls: z
    .array(
      z.object({
        bucketPath: z.string().min(1).max(500),
        caption: z
          .string()
          .max(TEXT_FIELD_CONSTRAINTS.photoCaption.max)
          .optional(),
      })
    )
    .max(SURVEY_FIELD_LIMITS.photoUrls.max)
    .optional(),
  /**
   * Story plot/narrative description - REQUIRED.
   * This is the main story content that describes what happens in the book.
   * Can be a predefined type (see STORY_PLOT_OPTIONS) or custom detailed plot.
   * Examples: 'biographical', 'hero_journey', or a full custom story description.
   */
  plot: z
    .string()
    .min(TEXT_FIELD_CONSTRAINTS.plot.min)
    .max(TEXT_FIELD_CONSTRAINTS.plot.max),
  /**
   * Structured plot type selection from STORY_IDEA_OPTIONS (themes + plots + custom).
   * Optional for backward compatibility with existing orders.
   */
  plotType: z.enum(STORY_IDEA_OPTIONS).optional(),
  /**
   * Free-form additional details or special instructions from the customer.
   * Optional, max 4000 characters.
   */
  additionalDetails: z
    .string()
    .max(TEXT_FIELD_CONSTRAINTS.additionalDetails.max)
    .optional(),
});

export const companionPreferencesSchema = z.object({
  magicalCompanion: z.enum(MAGICAL_COMPANION_OPTIONS).optional(),
  realWorldSidekicks: z
    .array(realWorldCompanionSchema)
    .max(SURVEY_FIELD_LIMITS.realWorldSidekicks.max),
});

export const deliveryPreferencesSchema = z.object({
  email: z.string().email().optional(),
  bookSize: z.enum(BOOK_SIZE_OPTIONS).default(DEFAULT_BOOK_SIZE),
  purchaseType: z.enum(PURCHASE_TYPE_OPTIONS).default('digital_only'),
  marketingOptIn: z.boolean().default(false),
  tosAcceptedVersion: z.string().optional(),
  privacyPolicyAcceptedVersion: z.string().optional(),
  biometricConsentVersion: z.string().optional(),
});

export const storyGenerationSurveySchema = z.object({
  child: childProfileSchema,
  preferences: storyPreferencesSchema,
  companions: companionPreferencesSchema,
  delivery: deliveryPreferencesSchema,
});

export type ChildProfile = z.infer<typeof childProfileSchema>;
export type StoryPreferences = z.infer<typeof storyPreferencesSchema>;
export type CompanionPreferences = z.infer<typeof companionPreferencesSchema>;
export type DeliveryPreferences = z.infer<typeof deliveryPreferencesSchema>;
export type StoryGenerationSurvey = z.infer<typeof storyGenerationSurveySchema>;
export type RealWorldCompanion = z.infer<typeof realWorldCompanionSchema>;

export function parseStoryGenerationSurvey(
  data: unknown
): StoryGenerationSurvey {
  // Merge customTheme → theme for backward compat with orders saved before the
  // client-side fix. customTheme is a UI-only field that Zod strips.
  if (typeof data === 'object' && data !== null && 'preferences' in data) {
    const prefs = (data as Record<string, Record<string, unknown>>).preferences;
    if (
      typeof prefs?.customTheme === 'string' &&
      prefs.customTheme &&
      !prefs.theme
    ) {
      prefs.theme = prefs.customTheme;
    }
  }
  const parsed = storyGenerationSurveySchema.parse(data);
  // Clamp targetStoryLength to valid range for the purchase type.
  // This ensures backward compatibility with legacy orders that may have
  // page counts outside the new book-type-specific limits.
  const limits = getPageCountLimits(parsed.delivery.purchaseType);
  const pages = parsed.preferences.targetStoryLength;
  if (pages < limits.min || pages > limits.max) {
    parsed.preferences.targetStoryLength = Math.max(
      limits.min,
      Math.min(limits.max, pages)
    );
  }
  return parsed;
}
